import React from 'react';
import { Container, Typography, Box, Card, CardContent, TextField, Button, Link as MuiLink, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Handshake as HandshakeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomerLayout from '../../components/customer/CustomerLayout';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

// Soporte del portal cliente. OJO: el FAQ «clásico» habla ENTERO de planes,
// Paddle, métodos de pago, reembolsos y facturas — es la superficie de pago que
// quedaba abierta a un cliente GESTIONADO por un partner (Soporte sí está en su
// menú). Con partner se sustituye por un FAQ de uso (empleados, usuarios,
// créditos) y por el contacto real: su partner, no un formulario del
// marketplace con el que ese cliente no tiene relación.

const CustomerSupportPage: React.FC = () => {
  const { t } = useTranslation();
  const { managedByReseller } = useCustomerPortal();
  const partnerName = managedByReseller?.name ?? '';
  const partnerEmail = managedByReseller?.contact_email ?? '';

  const billingFaqs = [
    { question: t('customer.support.faq1Question'), answer: t('customer.support.faq1Answer') },
    { question: t('customer.support.faq2Question'), answer: t('customer.support.faq2Answer') },
    { question: t('customer.support.faq3Question'), answer: t('customer.support.faq3Answer') },
    { question: t('customer.support.faq4Question'), answer: t('customer.support.faq4Answer') },
    { question: t('customer.support.faq5Question'), answer: t('customer.support.faq5Answer') },
    { question: t('customer.support.faq6Question'), answer: t('customer.support.faq6Answer') },
    { question: t('customer.support.faq7Question'), answer: t('customer.support.faq7Answer') },
    { question: t('customer.support.faq8Question'), answer: t('customer.support.faq8Answer') }
  ];

  // FAQ de una cuenta gestionada: solo lo que ese cliente puede hacer de
  // verdad; todo lo administrativo remite —con nombre y correo— a su partner.
  const managedFaqs = [
    {
      question: t('customer.support.managedFaq1Question', '¿Cómo abro mi empleado IA?'),
      answer: t('customer.support.managedFaq1Answer', 'En «Mi cuenta», cuando el empleado aparezca como Activo, pulsa «Abrir»: su aplicación se abre en una pestaña nueva y ya puedes trabajar con él.')
    },
    {
      question: t('customer.support.managedFaq2Question', '¿Cómo añado a alguien de mi equipo?'),
      answer: t('customer.support.managedFaq2Answer', 'En «Mi cuenta» → «Usuarios» das de alta a la persona y la asignas al empleado que necesite usar. La contraseña generada se muestra una sola vez y también se le envía por correo.')
    },
    {
      question: t('customer.support.managedFaq3Question', '¿Qué son los créditos?'),
      answer: t('customer.support.managedFaq3Answer', 'Son el saldo con el que trabajan tus empleados IA: solo baja con el consumo real. En «Mi cuenta» ves el disponible, lo consumido y el detalle de cada movimiento.')
    },
    {
      question: t('customer.support.managedFaq4Question', '¿Cómo consigo más créditos o más empleados?'),
      answer: partnerEmail
        ? t('customer.support.managedFaq4Answer', 'Escribe a {{name}} ({{email}}): ellos gestionan tu saldo y tu cupo de empleados.', { name: partnerName, email: partnerEmail })
        : t('customer.support.managedFaq4AnswerNoEmail', 'Escribe a {{name}}: ellos gestionan tu saldo y tu cupo de empleados.', { name: partnerName })
    },
    {
      question: t('customer.support.managedFaq5Question', '¿Qué pasa si me quedo sin créditos?'),
      answer: t('customer.support.managedFaq5Answer', 'Tus empleados dejan de trabajar hasta que haya saldo. El portal te avisa antes, cuando el disponible baja del 10 %, para que puedas pedir una ampliación a tiempo.')
    },
    {
      question: t('customer.support.managedFaq6Question', '¿Dónde veo mi factura?'),
      answer: t('customer.support.managedFaq6Answer', 'Tu cuenta la gestiona {{name}}, así que toda la parte comercial y administrativa (contratación, facturas, pagos) va con ellos: en este portal no hay planes ni pagos.', { name: partnerName })
    }
  ];

  const faqs = managedByReseller ? managedFaqs : billingFaqs;

  return (
    <CustomerLayout>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          {t('customer.support.title')}
        </Typography>

        {managedByReseller ? (
          <Card elevation={0} sx={{ mb: 4, border: '1px solid', borderColor: 'primary.light', bgcolor: '#f4f9ff', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <HandshakeIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('customer.support.managedTitle', 'Tu cuenta la gestiona {{name}}', { name: partnerName })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('customer.support.managedHelp', 'Para cualquier duda, ampliación de créditos o de empleados, tu contacto es {{name}}. Te atenderán directamente.', { name: partnerName })}
                  </Typography>
                  {partnerEmail && (
                    <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }}>
                      <MuiLink href={`mailto:${partnerEmail}`} underline="hover">
                        {partnerEmail}
                      </MuiLink>
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('customer.support.contactUs')}</Typography>
              <Box component="form">
                <TextField fullWidth label={t('customer.support.yourEmail')} margin="normal" />
                <TextField fullWidth label={t('customer.support.subject')} margin="normal" />
                <TextField fullWidth label={t('customer.support.message')} multiline rows={4} margin="normal" />
                <Button
                  variant="contained"
                  sx={{
                    mt: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none'
                  }}
                >
                  {t('customer.support.sendMessage')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('customer.support.faq')}
        </Typography>
        {faqs.map((faq, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </CustomerLayout>
  );
};

export default CustomerSupportPage;
